import { AddOutlined, FamilyRestroomOutlined, RemoveCircleOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { ChoiceControl, CompactEditButton, DefinitionList, EmptyState, FormDialog, FormTextField, LoadingIndicator, SectionCard, ValueBadge, designTokens } from '../../../design-system';
import type { ProfileChange, ProfileChangeDetail, ProfileFamilyChangeInput, Relative, RelativeRelationshipType } from './model';
import type { FamilyProfilePort } from './port';
import { ProfilePendingChanges } from './ProfilePendingChanges';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { profileDateValue, profileTextValue } from './profileDisplay';

type ChangeAction = 'CREATE' | 'UPDATE' | 'END';
const sectionInset = { xs: `${designTokens.layout.surfacePaddingCompact}px`, sm: `${designTokens.layout.surfacePadding}px` };

export type PersonFamilySectionProps = {
  port: FamilyProfilePort;
  personId?: string;
  canRead: boolean;
  canWrite: boolean;
  canApprove?: boolean;
  fixtureMode: boolean;
  fixtureData?: Relative[];
};

const relationshipOptions: Array<{ value: RelativeRelationshipType; label: string }> = [
  { value: 'SPOUSE', label: '배우자' },
  { value: 'CHILD', label: '자녀' },
  { value: 'PARENT', label: '부모' },
  { value: 'SIBLING', label: '형제자매' },
  { value: 'GUARDIAN', label: '보호자' },
  { value: 'OTHER', label: '기타' },
];
const emptyFixtureData: Relative[] = [];

function relationshipLabel(value: string) {
  return relationshipOptions.find((option) => option.value === value)?.label ?? value;
}

function actionLabel(action: string) {
  return ({ CREATE: '추가', UPDATE: '수정', END: '종료' } as Record<string, string>)[action] ?? action;
}

function today() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function operationKeys() {
  return { create: crypto.randomUUID(), submit: crypto.randomUUID() };
}

function upsertChange(changes: ProfileChange[], next: ProfileChange) {
  return [...changes.filter((change) => change.id !== next.id), next];
}

function setTransitioning(setter: Dispatch<SetStateAction<Set<string>>>, id: string, active: boolean) {
  setter((current) => {
    const next = new Set(current);
    if (active) next.add(id);
    else next.delete(id);
    return next;
  });
}

function stableKey(keys: Map<string, string>, id: string) {
  const existing = keys.get(id);
  if (existing) return existing;
  const created = crypto.randomUUID();
  keys.set(id, created);
  return created;
}

export function PersonFamilySection({
  port,
  personId,
  canRead,
  canWrite,
  canApprove = false,
  fixtureMode,
  fixtureData = emptyFixtureData,
}: PersonFamilySectionProps) {
  const resource = useAsyncResource(
    async () => {
      if (!canRead) return { values: [], pending: [] };
      if (fixtureMode) return { values: fixtureData, pending: [] };
      const [family, changes] = await Promise.all([
        personId ? port.getPersonFamily(personId) : port.getMyFamily(),
        personId ? port.listPersonFamilyChanges(personId) : port.listMyFamilyChanges(),
      ]);
      return { values: family.data, pending: changes.data };
    },
    [canRead, fixtureData, fixtureMode, personId, port],
    '정보를 불러오지 못했습니다.',
  );
  const keys = useRef(operationKeys());
  const lastCreateFingerprint = useRef<string | undefined>(undefined);
  const submitKeys = useRef(new Map<string, string>());
  const approveKeys = useRef(new Map<string, string>());
  const localDetails = useRef(new Map<string, ProfileChangeDetail>());
  const [action, setAction] = useState<ChangeAction>();
  const [selected, setSelected] = useState<Relative>();
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [relationshipType, setRelationshipType] = useState<RelativeRelationshipType>('SPOUSE');
  const [legalName, setLegalName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [dependent, setDependent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [localPending, setLocalPending] = useState<ProfileChange[]>([]);
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [transitionErrors, setTransitionErrors] = useState<Record<string, string>>({});
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [approvedFeedback, setApprovedFeedback] = useState<ProfileChange>();

  useEffect(() => {
    if (fixtureMode || !resource.data) return;
    setLocalPending([]);
  }, [fixtureMode, resource.data]);

  useEffect(() => {
    if (!approvedFeedback) return;
    const timer = window.setTimeout(() => setApprovedFeedback(undefined), 5000);
    return () => window.clearTimeout(timer);
  }, [approvedFeedback]);

  if (!canRead) return null;

  const openChange = (nextAction: ChangeAction, relative?: Relative) => {
    keys.current = operationKeys();
    lastCreateFingerprint.current = undefined;
    setAction(nextAction);
    setSelected(relative);
    setEffectiveDate(today());
    setRelationshipType((relative?.relationshipType as RelativeRelationshipType) || 'SPOUSE');
    setLegalName(relative?.legalName || '');
    setBirthDate(relative?.birthDate || '');
    setDependent(Boolean(relative?.dependent));
    setError(undefined);
  };

  const closeDialog = () => {
    if (busy) return;
    setAction(undefined);
    setSelected(undefined);
    setError(undefined);
  };

  const submitPending = (change: ProfileChange) => {
    if (change.status !== 'DRAFT' || !canWrite) return;
    setTransitioning(setSubmittingIds, change.id, true);
    setTransitionErrors((current) => {
      const next = { ...current };
      delete next[change.id];
      return next;
    });
    const request = fixtureMode
      ? Promise.resolve<ProfileChange>({ ...change, status: 'IN_PROGRESS', submittedAt: new Date().toISOString() })
      : personId
        ? port.submitPersonFamilyChange(personId, change.id, stableKey(submitKeys.current, change.id))
        : port.submitFamilyChange(change.id, stableKey(submitKeys.current, change.id));
    void request.then((submitted) => {
      setLocalPending((current) => upsertChange(current, submitted));
      resource.reload();
    }, (reason: unknown) => {
      setLocalPending((current) => upsertChange(current, { ...change, status: 'DRAFT' }));
      setTransitionErrors((current) => ({
        ...current,
        [change.id]: reason instanceof Error ? reason.message : '가족정보 초안을 승인 요청으로 제출하지 못했습니다.',
      }));
    }).finally(() => setTransitioning(setSubmittingIds, change.id, false));
  };

  const approvePending = (change: ProfileChange) => {
    if (!personId || change.status !== 'IN_PROGRESS' || !canApprove) return;
    setTransitioning(setApprovingIds, change.id, true);
    setTransitionErrors((current) => {
      const next = { ...current };
      delete next[change.id];
      return next;
    });
    const request = fixtureMode
      ? Promise.resolve<ProfileChange>({ ...change, status: 'APPROVED', approvedAt: new Date().toISOString() })
      : port.approvePersonFamilyChange(personId, change.id, stableKey(approveKeys.current, change.id));
    void request.then((approved) => {
      setLocalPending((current) => current.filter((item) => item.id !== approved.id));
      setResolvedIds((current) => new Set(current).add(approved.id));
      setApprovedFeedback(approved);
      resource.reload();
    }, (reason: unknown) => {
      setTransitionErrors((current) => ({
        ...current,
        [change.id]: reason instanceof Error ? reason.message : '가족정보 변경 요청을 승인하지 못했습니다.',
      }));
    }).finally(() => setTransitioning(setApprovingIds, change.id, false));
  };

  const submit = () => {
    if (!action) return;
    if (!effectiveDate) {
      setError('효력일을 입력해 주세요.');
      return;
    }
    if (action !== 'CREATE' && !selected) {
      setError('변경할 가족정보를 다시 선택해 주세요.');
      return;
    }
    if (action !== 'END' && (!relationshipType || !legalName.trim())) {
      setError('관계와 이름을 입력해 주세요.');
      return;
    }
    if (birthDate && birthDate > today()) {
      setError('생년월일은 오늘 이후일 수 없습니다.');
      return;
    }

    const input: ProfileFamilyChangeInput = {
      effectiveDate,
      family: action === 'END'
        ? { action, relative: { id: selected!.id } }
        : {
          action,
          relative: action === 'UPDATE' ? { id: selected!.id } : undefined,
          relationshipType,
          legalName: legalName.trim(),
          birthDate: birthDate || undefined,
          dependent,
        },
    };
    const fingerprint = JSON.stringify(input);
    if (lastCreateFingerprint.current && lastCreateFingerprint.current !== fingerprint) keys.current = operationKeys();
    lastCreateFingerprint.current = fingerprint;
    setBusy(true);
    setError(undefined);
    const create = fixtureMode
      ? Promise.resolve<ProfileChange>({ id: 'fixture-family-change', changeType: 'RELATIVE', status: 'DRAFT', effectiveDate })
      : personId
        ? port.createPersonFamilyChange(personId, input, keys.current.create)
        : port.createFamilyChange(input, keys.current.create);
    void create.then((draft) => {
      submitKeys.current.set(draft.id, keys.current.submit);
      localDetails.current.set(draft.id, { ...draft, details: { family: input.family } });
      setLocalPending((current) => upsertChange(current, draft));
      setTransitionErrors((current) => {
        const next = { ...current };
        delete next[draft.id];
        return next;
      });
      setBusy(false);
      setAction(undefined);
      setSelected(undefined);
      submitPending(draft);
    }, (reason: unknown) => {
      setError(reason instanceof Error ? reason.message : '가족정보 변경 초안을 저장하지 못했습니다.');
      setBusy(false);
    });
  };

  const pendingById = new Map((resource.data?.pending ?? []).map((change) => [change.id, change]));
  for (const change of localPending) pendingById.set(change.id, change);
  const pending = Array.from(pendingById.values()).filter((change) => !resolvedIds.has(change.id) && (change.status === 'DRAFT' || change.status === 'IN_PROGRESS'));
  const values = resource.data?.values ?? [];
  const dialogTitle = action === 'CREATE' ? '가족정보 추가 요청' : action === 'UPDATE' ? '가족정보 수정 요청' : '가족정보 종료 요청';
  const loadDetails = (change: ProfileChange) => {
    const local = localDetails.current.get(change.id);
    if (local) return Promise.resolve(local);
    return personId ? port.getPersonFamilyChange(personId, change.id) : port.getMyFamilyChange(change.id);
  };
  const renderDetails = (detail: ProfileChangeDetail) => {
    const family = detail.details.family;
    if (!family) return <Alert severity="warning">가족정보 변경 내용을 확인할 수 없습니다.</Alert>;
    const currentRelative = family.relative?.id ? values.find((relative) => relative.id === family.relative?.id) : undefined;
    const missingCurrentTarget = family.action !== 'CREATE' && !currentRelative;
    const currentRelativeName = currentRelative?.protected ? '보호된 가족 정보' : currentRelative?.legalName || '이름 미등록';
    const target = currentRelative
      ? `${currentRelativeName} · ${relationshipLabel(currentRelative.relationshipType)}`
      : family.relative?.descriptor || (family.action === 'CREATE' ? '새 가족 정보' : '대상 가족 정보 없음');
    const items: Array<readonly [string, ReactNode]> = [
      ['변경 유형', <ValueBadge label={actionLabel(family.action)} />],
      ['대상', target],
    ];
    if (family.action !== 'END') items.push(
      ['관계', family.relationshipType ? <ValueBadge label={relationshipLabel(family.relationshipType)} /> : undefined],
      ['이름', profileTextValue(family.legalName) || '미입력'],
      ['생년월일', profileDateValue(family.birthDate) || '미입력'],
      ['부양가족', <ValueBadge label={family.dependent ? '예' : '아니요'} />],
    );
    return <Stack spacing={1.25}>
      <DefinitionList items={items} />
      {missingCurrentTarget && <Alert severity="warning">현재 원장에서 대상을 확인할 수 없어 처리할 수 없습니다.</Alert>}
    </Stack>;
  };

  return <>
    <SectionCard
      title={<><FamilyRestroomOutlined aria-hidden sx={{ mr: 1, fontSize: 20, color: 'text.secondary', verticalAlign: 'text-bottom' }} />가족 정보</>}
      action={canWrite && !resource.loading ? <Button size="small" variant="outlined" startIcon={<AddOutlined />} onClick={() => openChange('CREATE')}>가족 추가</Button> : undefined}
      headerPadding="standard"
    >
      {approvedFeedback && <Box sx={{ px: sectionInset, pt: 1.5 }}>
        <Alert severity="success">
          <Typography variant="body2" sx={{ fontWeight: 720 }}>가족정보 변경이 승인되어 원장에 반영됐습니다.</Typography>
          <Typography variant="caption">승인 완료</Typography>
        </Alert>
      </Box>}
      <ProfilePendingChanges
        changes={pending}
        label="가족정보"
        canSubmit={canWrite}
        canApprove={Boolean(personId && canApprove)}
        submittingIds={submittingIds}
        approvingIds={approvingIds}
        errors={transitionErrors}
        loadDetails={loadDetails}
        renderDetails={renderDetails}
        isActionableDetail={(detail) => {
          const family = detail.details.family;
          if (!family) return false;
          if (family.action === 'CREATE') return true;
          return Boolean(family.relative?.id && values.some((relative) => relative.id === family.relative?.id));
        }}
        onSubmit={submitPending}
        onApprove={approvePending}
      />
      {resource.loading ? <Box sx={{ minHeight: 150, display: 'grid', placeItems: 'center' }}><LoadingIndicator label="가족정보 불러오는 중" /></Box>
        : resource.error ? <Box sx={{ p: sectionInset }}><Alert severity="error" action={<Button color="inherit" size="small" onClick={resource.reload}>다시 시도</Button>}>{resource.error.message}</Alert></Box>
          : values.length ? <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {values.map((relative) => {
              const name = relative.protected ? '보호된 가족 정보' : relative.legalName || '이름 미등록';
              return <Stack key={relative.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ px: sectionInset, py: 1.35, alignItems: { sm: 'center' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}><Typography variant="body2" sx={{ fontWeight: 720 }}>{name}</Typography><ValueBadge label={relationshipLabel(relative.relationshipType)} />{relative.dependent && <ValueBadge label="부양가족" />}</Stack>
                  <Typography variant="caption" color="text.secondary">{[relative.birthDate, relative.protected ? '민감정보 권한 필요' : undefined].filter(Boolean).join(' · ')}</Typography>
                </Box>
                {canWrite && !relative.protected && <Stack direction="row" spacing={0.5}>
                  <CompactEditButton aria-label={`${name} 가족정보 수정`} onClick={() => openChange('UPDATE', relative)}>수정</CompactEditButton>
                  <Button size="small" color="error" startIcon={<RemoveCircleOutlined />} aria-label={`${name} 가족정보 종료`} onClick={() => openChange('END', relative)}>종료</Button>
                </Stack>}
              </Stack>;
            })}
          </Stack>
            : <EmptyState>등록된 가족정보가 없습니다.</EmptyState>}
    </SectionCard>

    <FormDialog
      open={Boolean(action)}
      onClose={closeDialog}
      onSubmit={submit}
      title={dialogTitle}
      help={action === 'END' ? undefined : '제출 후 승인되기 전까지 현재 가족정보는 바뀌지 않습니다.'}
      cancelLabel="취소"
      submitLabel="승인 요청"
      busy={busy}
      busyLabel="초안 저장 중…"
      submitColor={action === 'END' ? 'error' : 'primary'}
      maxWidth="sm"
    >
        <Stack spacing={1.5}>
          {action === 'END' && <Alert severity="warning">승인되면 이 가족정보의 현재 기록이 종료됩니다. 기록 자체는 삭제되지 않습니다.</Alert>}
          {action === 'END' && selected ? <Typography variant="body2"><strong>{selected.legalName || relationshipLabel(selected.relationshipType)}</strong> 정보를 종료 요청합니다.</Typography> : <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormTextField select required fullWidth label="관계" value={relationshipType} onChange={(event) => setRelationshipType(event.target.value as RelativeRelationshipType)}>
                {relationshipOptions.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label} /></MenuItem>)}
              </FormTextField>
              <FormTextField required fullWidth label="이름" labelSuffix={`${legalName.length}/200`} value={legalName} onChange={(event) => setLegalName(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
              <FormTextField fullWidth type="date" label="생년월일" value={birthDate} onChange={(event) => setBirthDate(event.target.value)} />
              <ChoiceControl kind="checkbox" sx={{ minWidth: 150 }} checked={dependent} onCheckedChange={setDependent} label="부양가족" />
            </Stack>
          </>}
          <FormTextField required type="date" label="효력일" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
    </FormDialog>
  </>;
}
